import searchActionTypes from '@nykaa/nes-listing-shared/search/store/actionTypes';
import interactionData from '@nykaa/nes-listing-shared/constants/interactionData';
import { Action } from 'shared/store/types';
import actionTypes from './actionTypes';
import { SearchListingState } from './types';
import { setFilterApiHitCount } from 'helpers/utils/filterApiHitCount';

const defaultRedirection = {
  isRedirect: false,
  redirectUrl: '',
  title: '',
  subtitle: '',
  imageUrl: '',
  type: '',
  deeplink: '',
};

const defaultState: SearchListingState = {
  activeFilters: {},
  error: false,
  errorFetchingPage: false,
  isFilterChanged: false,
  isPageChanged: false,
  loading: false,
  loadingPage: false,
  pageNumber: 0,
  searchTerm: '',
  listingData: null,
  redirect: defaultRedirection,
  isClearAll: false,
  isEmptyWidgetLoading: false,
  isEmptyWidgetError: false,
  emptyStateWidgets: null,
  isEmptyRangeError: false,
  inlineFilters: {},
  remaining_inventories_ids: [],
  interactionData,
  isProductListLoaded: false,
  isBestPriceLoading: true,
  bestPriceGradient: [],
  bestPriceColor: '',
  bestOfferColor: '',
  inlineWidgets: null,
};

const REDUCER_NAME = (
  state = defaultState,
  action: Action
): SearchListingState => {
  const { type, payload } = action;
  switch (type) {
    case actionTypes.FETCH_PROGRESS:
      return {
        ...state,
        error: false,
        errorFetchingPage: false,
        isFilterChanged: false,
        loading: true,
        loadingPage: false,
        isProductListLoaded: false,
      };

    case actionTypes.FETCH_SUCCESS:
      return {
        ...state,
        error: false,
        loading: false,
        pageNumber: 1,
        listingData: payload.listingData,
        searchTerm: payload.searchTerm,
        activeFilters: payload.activeFilters,
        isClearAll: false,
        isEmptyRangeError: false,
        isProductListLoaded: true,
      };

    case actionTypes.FETCH_EMPTY_RANGE_ERROR:
      return {
        ...state,
        error: false,
        loading: false,
        isEmptyRangeError: true,
      };
    case actionTypes.IS_REDIRECT:
      return {
        ...state,
        redirect: payload.redirect,
      };
    case actionTypes.RESET_REDIRECT:
      return {
        ...state,
        redirect: defaultRedirection,
      };
    case actionTypes.FETCH_ERROR:
      return {
        ...state,
        error: true,
        loading: false,
        isProductListLoaded: false,
      };

    case actionTypes.FETCHING_ERROR_CLIENT:
      return {
        ...state,
        loading: false,
      };

    case actionTypes.FILTER_CHANGE: {
      setFilterApiHitCount();
      const { activeFilters: activeFiltersSate } = state;
      return {
        ...state,
        activeFilters: {
          ...payload.newFilter,
          sort: activeFiltersSate.sort,
        },
        isClearAll: payload.isClearAll,
        isFilterChanged: true,
        isEmptyWidgetError: false,
      };
    }

    case actionTypes.RESET_FILTERS:
      return {
        ...state,
        activeFilters: payload,
      };

    case actionTypes.CHANGE_FILTER_API_STATUS: {
      return {
        ...state,
        listingData: {
          ...state.listingData,
          isApiFailed: payload,
        },
      };
    }

    case actionTypes.CLICK_INTERACTION:
      return {
        ...state,
        interactionData: payload,
      };

    case actionTypes.SORT_CHANGE: {
      const { activeFilters: activeFiltersSate } = state;

      return {
        ...state,
        activeFilters: {
          ...activeFiltersSate,
          sort: payload,
        },
        isFilterChanged: true,
      };
    }

    case actionTypes.CHANGE_PAGE:
      return {
        ...state,
        isPageChanged: true,
      };

    case actionTypes.FETCH_PAGE_START:
      return {
        ...state,
        errorFetchingPage: false,
        isPageChanged: false,
        loadingPage: true,
        isProductListLoaded: false,
      };

    case actionTypes.FETCH_PAGE_SUCCESS: {
      const currentListingProducts = state.listingData?.products || [];
      const newListingData = {
        ...state.listingData,
        products: [...currentListingProducts, ...payload.products],
        stopFurtherCall: payload.stopFurtherCall,
      };

      return {
        ...state,
        loadingPage: false,
        listingData: newListingData,
        pageNumber: state.pageNumber + 1,
        isProductListLoaded: true,
      };
    }

    case actionTypes.FETCH_PAGE_ERROR:
      return {
        ...state,
        errorFetchingPage: true,
        loadingPage: false,
        isProductListLoaded: false,
      };

    case actionTypes.FETCH_EMPTY_STATE_PROGRESS:
      return {
        ...state,
        isEmptyWidgetLoading: true,
      };

    case actionTypes.FETCH_EMPTY_STATE_SUCCESS: {
      const { widgets, remaining_inventories_ids } = payload;
      return {
        ...state,
        emptyStateWidgets: widgets,
        remaining_inventories_ids,
        isEmptyWidgetLoading: false,
      };
    }

    case actionTypes.FETCH_EMPTY_STATE_FAILED:
      return {
        ...state,
        isEmptyWidgetError: true,
      };

    case actionTypes.IS_FILTER_OPEN:
      return {
        ...state,
        isFilterOpen: payload.isFilterOpen,
        activeFilterType: payload.activeFilterType,
      };
    case searchActionTypes.FETCH_INLINE_WIDGET_SUCCESS: {
      const { widgets } = payload;
      return {
        ...state,
        inlineWidgets: widgets,
      };
    }

    case searchActionTypes.FETCH_BEST_PRICE_PROGRESS:
      return {
        ...state,
        isBestPriceLoading: true,
      };

    case searchActionTypes.FETCH_BEST_PRICE_FAILED: {
      const { listingData } = state;
      const updatedListingProducts = listingData?.products?.map((product) => ({
        ...product,
        isBestPriceLoaded: true,
      }));
      return {
        ...state,
        isBestPriceLoading: false,
        listingData: {
          ...listingData,
          products: updatedListingProducts || [],
        },
      };
    }

    case searchActionTypes.FETCH_BEST_PRICE_SUCCESS: {
      const { listingData } = state;
      const { bestOffers, gradientColor, textColor, offerTextColor } =
        payload || {};
      const updatedListingProducts = listingData?.products?.map((product) => {
        const { id } = product;
        return {
          ...product,
          isBestPriceLoaded: true,
          ...bestOffers[id],
        };
      });
      return {
        ...state,
        isBestPriceLoading: false,
        bestPriceGradient: gradientColor,
        bestPriceColor: textColor,
        bestOfferColor: offerTextColor,
        listingData: {
          ...listingData,
          products: updatedListingProducts || [],
        },
      };
    }

    default:
      return state;
  }
};

export default REDUCER_NAME;
